/* eslint-disable @typescript-eslint/naming-convention */

import {
  RecordEmpty,
  RecordObject
} from '../../models/common.models';
import {
  AssessmentGoalDto,
  AssessmentSubmissionDto
} from '../eols-assessment-service/eols-assessment-service.dtos';

export enum RemediationActivityTypeDto {
  REVIEW_BY_TAXON = 'REVIEW_BY_TAXON',
  APPLY_BY_CONTENT_TYPE = 'APPLY_BY_CONTENT_TYPE',
  ASSESS_BY_CONTENT_TYPE = 'ASSESS_BY_CONTENT_TYPE',
}

export type RecommendationScoreRange = {
  minScore: number;
  numOfItems: number;
}

export enum RemediationAssignmentSettingsTypeDto {
  HESI_EXAM_SETTINGS_V1 = 'HESI_EXAM_SETTINGS_V1',
  SPARK_EXAM_SETTINGS_V1 = 'SPARK_EXAM_SETTINGS_V1',
  UNSTRUCTURED = 'UNSTRUCTURED',
}

export enum RemediationAssignmentSourceTypeDto {
  HESI_EXAM_EDITION__EDITION_ID = 'HESI_EXAM_EDITION__EDITION_ID',
}

export type RemediationActivityTypeSettings = {
  activityType: RemediationActivityTypeDto;
  isActive: boolean;
  recommendationMultiplier: number;
  recommendationAdder: number;
  recommendationScoreRanges: RecommendationScoreRange[];
  contentSettings: {
    contentType: RecommendationContentTypeDto;
    isActive: boolean;
    minScoreToPass: number;
    recommendationItemScoreRanges: RecommendationScoreRange[];
  }[];
};

export type HesiExamRemediationSettingsDto = {
  categoryHierarchyGoals: {
    catName: string;
    categoryGroupId: number;
    categoryHierarchyId: number;
  }[];
  activityTypes: RemediationActivityTypeSettings[];
  scheduledStartDate: string;
  scheduledEndDate: string;
  enableSherpathAiChat?: boolean;
}

export type SparkExamRemediationSettingsDto = {
  taxonGoals: {
    taxonId: string;
    taxonName: string;
    rootTaxonId: string;
  }[];
  activityTypes: RemediationActivityTypeSettings[];
  enableSherpathAiChat?: boolean;
}

export type RemediationAssignmentSettingsDto = ({
  type: RemediationAssignmentSettingsTypeDto.HESI_EXAM_SETTINGS_V1;
} & HesiExamRemediationSettingsDto) | ({
  type: RemediationAssignmentSettingsTypeDto.SPARK_EXAM_SETTINGS_V1;
} & SparkExamRemediationSettingsDto) | ({
  type: RemediationAssignmentSettingsTypeDto.UNSTRUCTURED;
} & RecordEmpty);

export type RemediationAssignmentDto = {
  id: number;
  assignmentId: number;
  createdByUser: number;
  updatedByUser: number;
  courseSectionId: number;
  settings: RemediationAssignmentSettingsDto;
  createdAt: string;
  updatedAt: string;
}

export type RemediationAssignmentWithSourcesDto = {
  eolsAssignment: {
    title: string;
    dueDate: string;
  };
  remediationAssignment: RemediationAssignmentDto;
  sourceList: AssignmentSourceDto[];
}

export enum RecommendationContentTypeDto {
  OSMOSIS_VIDEO = 'OSMOSIS_VIDEO',
  EBOOK_READING = 'EBOOK_READING',
  TOPIC_OVERVIEW = 'TOPIC_OVERVIEW',
  HESI_CASE_STUDY = 'HESI_CASE_STUDY',
  RAAS_QUIZ = 'RAAS_QUIZ',
}

export enum RecommendationDataTypeDto {
  EBOOK_DATA_V1 = 'EBOOK_DATA_V1',
  OSMOSIS_VIDEO_DATA_V1 = 'OSMOSIS_VIDEO_DATA_V1',
}

export type EBookRecommendationDataDto = {
  breadCrumbs: {
    id: string;
    page_range: number[];
    title: string;
    type: string;
    cfi?: string;
  }[];
  isbn: string;
  title: string;
}

export enum RecommendationGenerationStatusDto {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  NOT_STARTED = 'NOT_STARTED',
  QUEUED = 'QUEUED',
  NOT_FOUND = 'NOT_FOUND',
  INVALID_ASSIGNMENT_GOALS = 'INVALID_ASSIGNMENT_GOALS',
  CANCELLED_TIMED_OUT = 'CANCELLED_TIMED_OUT',
  CANCELLED_ADMIN_REQUEST = 'CANCELLED_ADMIN_REQUEST',
  SELECTED = 'SELECTED',
}

export type AssessmentSourceDto = {
  id: number;
  sourceId: string;
  sourceType: string;
  raasAssessmentId: number;
  createdAt: string;
  updatedAt: string;
  score: number;
  scoreType: string;
}

export type AssignmentSourceDto = {
  id: number;
  sourceId: string;
  sourceType: string;
  remediationAssignmentId: number;
  createdAt: string;
  updatedAt: string;
}

export type RecommendationDto = {
  id: number;
  raasAssessmentId: number;
  userId: number;
  courseSectionId: number;
  contentType: RecommendationContentTypeDto;
  contentId: string;
  title: string;
  skipReason: string;
  skipExplanation: string;
  sourceRelevanceScore: number;
  displayOrder: number;
  estimatedTime: string; // Duration
  recommendationData: unknown;
  remediationAssignmentId: number;
  createdAt: string;
  updatedAt: string;
  assessmentGoalType: RemediationActivityTypeDto;
  assessmentGoalVtwId: string;
}

export enum RecommendationAttemptStatusDto {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED_FAILED = 'COMPLETED_FAILED',
  COMPLETED_PASSED = 'COMPLETED_PASSED',
}

export enum StatusWithDueDate {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED_FAILED = 'COMPLETED_FAILED',
  COMPLETED_PASSED = 'COMPLETED_PASSED',
  COMPLETED_FAILED_PAST_DUE = 'COMPLETED_FAILED_PAST_DUE',
  COMPLETED_PASSED_PAST_DUE = 'COMPLETED_PASSED_PAST_DUE',
}

export type RecommendationAttemptDto = {
  id: number;
  eolsAssessmentId: number;
  recommendationId: number;
  status: RecommendationAttemptStatusDto;
  createdAt: string;
  updatedAt: string;
  dateCompleted: string;
}

export type RecommendationAttemptSearchRequestDto = {
  ids?: number[];
  recommendationIds?: number[];
  eolsAssessmentIds?: number[];
}

export enum RecommendationItemTypeDto {
  EAQ_QUESTION = 'EAQ_QUESTION'
}

export type RecommendationItemDto = {
  id: number;
  itemType: RecommendationItemTypeDto;
  itemId: string;
  itemData: RecordObject;
  recommendationId: number;
  createdAt: string;
  updatedAt: string;
}

export type VectorSearchResultDto = {
  bread_crumbs: {
    id: string;
    type: string;
    title: string;
  }[];
  content_item_id: string;
  document_title: string;
  eid: string;
  source_id: string;
  source_id_type: string;
  location: string;
  title: string;
  content_type: string;
  content_id: string;
  chunk_text: string;
  chunk_id: string;
  content_props: {
    page: {
      end_page: {
        number: string;
        tag_id: string;
      };
      start_page: {
        number: string;
        tag_id: string;
      };
    };
    chapter_number: string;
  };
}

export type VectorSearchResponseDto = {
  query: string;
  results: {
    _source: VectorSearchResultDto;
    _score: string;
  }[];
  success: boolean;
  number_of_hits: number;
  total_time: number;
  service_version: string;
  model_name: string;
}

export type LlmResponseDto = {
  inputText: string;
  outputText: string;
  inputTokenCount: number;
  outputTokenCount: number;
  totalTime: string;
  version: string;
  errorMessage: string;
}

export enum LlmModels {
  claude_3_sonnet = 'claude_3_sonnet',
  claude_35_sonnet = 'claude_35_sonnet',
  claude_35_sonnet_v2 = 'claude_35_sonnet_v2',
  claude_3_haiku = 'claude_3_haiku',
  claude_35_haiku = 'claude_35_haiku',
}

export type VectorSearchDto = {
  queryText: string;
  filter: Record<string, unknown>;
  numberOfResultsToProvide: number;
  numberOfResultsToTry: number;
  minimumChunkLength: number;
}

export type LlmPromptDto = {
  prompt: string;
  model_id: LlmModels;
  temperature: number;
}

export type NewQuizSessionDto = {
  recommendationAttemptId: number;
  totalQuestions: number;
};

export type QuizSessionDto = NewQuizSessionDto & {
  id: number;
  isActive: boolean;
  eolsAssessmentId: number;
  createdAt: string;
  updatedAt: string;
};

export type QuestionSubmissionDetailsDto = {
  quizSessionId: number;
  recommendationAttemptId: number;
  userId: number;
  questions: QuizSessionQuestionDto[];
};

export type QuizAttemptDetailsDto = {
  correctQuestionThreshold: number;
  minScoreToPass: number;
  questionSubmissionDetails: QuestionSubmissionDetailsDto[];
  totalItems: number;
}

export type QuizSessionQuestionDto = {
  id: number;
  userId: number;
  courseSectionId: number;
  questionVtwId: string;
  questionPosition: number;
  correct: boolean;
  assessmentId: number;
  selectedAnswers: string;
  responseTime: string;
  requestTime: string;
  timeSpent: number;
  confident: boolean;
};

export type EbookReadingRequestDto = {
  isbn: string;
  startPage: string;
  endPage: string;
}

export type EbookSectionSsoRequestDto = {
  isbn: string;
  cfi: string;
}

export type EbookReadingResponseDto = {
  expires: string;
  signInUrl: string;
}

export type RaasAssessmentDto = {
  id: number;
  studentStudyEolsAssessmentId: number;
  remediationAssignmentId: number;
  courseSectionId: number;
  userId: number;
  isActive: boolean;
  recGenerationStatus: RecommendationGenerationStatusDto;
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  regeneratedFromId: number;
  regeneratedToId: number;
  lastAccessed: string; // ISO date string
  environment: string;
}

export type RaasAssessmentWithSourcesDto = {
  raasAssessment: RaasAssessmentDto;
  sourceList: RaasAssessmentSourceDto[];
}

export type RaasAssessmentWithAssignmentDto = {
  raasAssessment: RaasAssessmentDto;
  remediationAssignment: RemediationAssignmentWithSourcesDto;
}

export type RaasAssessmentPaginationDto = {
  content: RaasAssessmentWithSourcesDto[];
  pageable: {
    sort: {
      sorted: boolean;
      empty: boolean;
      unsorted: boolean;
    };
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    sorted: boolean;
    empty: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export type RegenerateResponseDto = {
  acceptedIds: number[];
  rejectedIds: number[];
  eolsAssessmentIds: number[];
}

export type HesiExamInfoDto = {
  editionId: number;
  examName: string;
  productName: string;
  programType: string;
  scoreType: string;
  examType: string;
  acceptableScore: string;
  recommendedScore: string;
}

export enum RaasAssessmentSourceTypeDto {
  HESI_USER_PERFORMANCE_EXAM = 'HESI_USER_PERFORMANCE_EXAM',    // Legacy type kept for backward compatibility in non-prod
  HESI_USER_PERFORMANCE_EXAM__TESTTAKER_GUID = 'HESI_USER_PERFORMANCE_EXAM__TESTTAKER_GUID',
  SPARK_EOLS_ASSESSMENT_ID = 'SPARK_EOLS_ASSESSMENT_ID',
}

export enum RaasAssessmentSourceScoreType {
  PERCENT_SCORE = 'PercentScore',
  RANGE_SCORE = 'RangeScore,',
  WEIGHT_SCORE = 'WeightScore',
  WEIGHTED_CHOICE_SCORE = 'WeightedChoiceScore',
  QUALITATIVE_SCORE = 'QualitativeScore',
}

export type RaasAssessmentSourceDto = {
  id: number;
  sourceType: RaasAssessmentSourceTypeDto;
  sourceId: string;
  raasAssessmentId: number;
  createdAt: string;
  updatedAt: string;
  score: number;
  scoreType: RaasAssessmentSourceScoreType;
}

export type ActivityTypeMetricsDto = {
  activityTypeTotalGoal: number;
  activityTypeTotalGoalCompleted: number;
  activityTypeTotalGoalProgress: number;
  activityTypeAttemptGoal: number;
  activityTypeAttemptGoalCompleted: number;
  timeSpent: string;
  progress: number;
}

export type RaasAssessmentMetricsDto = {
  eolsUserId: number;
  studentStudyEolsAssessmentId: number;
  // For HESI, this connects to TestTakerDataDto.scoringSystemId
  assessmentSources: AssessmentSourceDto[];
  remediationAssignmentId: number;
  assignmentSources: AssignmentSourceDto[];
  reviewContent: ActivityTypeMetricsDto;
  applyContent: ActivityTypeMetricsDto;
  assessContent: ActivityTypeMetricsDto;
  recommendationGenerationStatus: RecommendationGenerationStatusDto;
  studentStudyAssessmentSubmissions: AssessmentSubmissionDto[];
  totalProgress: number;
  assessmentGoalMetrics: {
    assessmentGoal: AssessmentGoalDto;
    categoryGroupName?: string | null;
    metrics: ActivityTypeMetricsDto;
  }[];
}

export type SherpathAIContextDto = {
  programType: string;
  taxons: string[];
}

export enum RemediationSourceType {
  OSMOSIS_VIDEO = 'OSMOSIS_VIDEO',
  EBOOK_CONTENT = 'EBOOK_CONTENT',
  HESI_CASE_STUDY = 'HESI_CASE_STUDY',
  EAQ_QUESTION = 'EAQ_QUESTION',
}

export type RecommendationSourceDto = {
  id: number;
  sourceType: RemediationSourceType;
  sourceId: string;
  recommendationId: number;
  createdAt: string;
  updatedAt: string;
}

export type RecommendationFeedbackDto = {
  recommendationId: number;
  isRelevant: boolean;
};
